import React, { useState, useEffect } from 'react';
import { Container, Box, Button, Typography, alpha } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import {
  Edit     as EditIcon,
  Delete   as DeleteIcon,
  AccountBalance as BudgetIcon,
  Person   as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  ShoppingCart  as ItemIcon,
  TrendingUp    as TrendingIcon,
  Schedule      as ScheduleIcon,
  Notes    as NotesIcon,
  Percent  as PercentIcon,
  Straighten    as UnitIcon,
  Numbers  as NumbersIcon,
  Receipt  as ReceiptIcon,
  ChevronRight  as ChevronRightIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Header            from '../../common/Header';
import Loading           from '../../common/Loading';
import FeedbackMessage   from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS }       from '../../common/SaaSTokens';
import { canEditBudget } from '../../../utils/financialPermissions';
import {
  injectDetailsPageStyles,
} from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import { formatSystemDateTime } from '../../../utils/systemDateFormat';

// â”€â”€â”€ Design tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const SURFACE_MID = '#0a1628';
const SURFACE_UP = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY = '#E2E8F0';
const TEXT_MUTED = '#64748B';

const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = '-', fullWidth = false }) => {
  const display = value || emptyLabel;
  const isEmpty = !value;
  return (
    <Box sx={{
      gridColumn: fullWidth ? '1 / -1' : undefined,
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '9px',
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem', color: isEmpty ? TEXT_MUTED : TEXT_BODY,
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const safePrice = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? '-' : `RM ${n.toFixed(2)}`;
};

const resolveUpdaterName = (budget) =>
  budget.last_updater?.name
  ?? budget.lastUpdater?.name
  ?? budget.updated_by_name
  ?? (typeof budget.updated_by === 'string' ? budget.updated_by : '-');

const CLAIM_STATUS_COLORS = {
  pending: NEON2,
  approved: NEON3,
  rejected: '#F87171',
};

const ClaimStatusBadge = ({ status }) => {
  const color = CLAIM_STATUS_COLORS[status] ?? TEXT_MUTED;
  const label = status ? String(status).charAt(0).toUpperCase() + String(status).slice(1) : 'Unknown';
  return (
    <Box sx={{
      px: 0.9, py: 0.25, borderRadius: '20px',
      background: alpha(color, 0.12),
      border: `1px solid ${alpha(color, 0.28)}`,
    }}>
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 700, color, letterSpacing: 0.4 }}>
        {label.toUpperCase()}
      </Typography>
    </Box>
  );
};

const formatClaimDate = (claim) => {
  const raw = claim.purchase_payment_date || claim.created_at;
  if (!raw) return '—';
  try {
    return format(new Date(raw), 'd MMM yyyy');
  } catch {
    return '—';
  }
};

// â”€â”€â”€ ActionButtons â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ActionButtons = ({ canEdit, canDelete, onEdit, onDelete }) => {
  if (!canEdit && !canDelete) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {canEdit && (
        <Button
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={onEdit}
          sx={{
            borderRadius: '10px', px: 2.5, py: 0.7,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
            background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`, color: NEON,
            transition: 'all 0.2s ease',
            '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.2)}` },
          }}
        >
          Edit
        </Button>
      )}
      {canDelete && (
        <Button
          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          onClick={onDelete}
          sx={{
            borderRadius: '10px', px: 2.5, py: 0.7,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
            background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.6)}`, color: '#F87171',
            transition: 'all 0.2s ease',
            '&:hover': { background: alpha('#F87171', 0.08), borderColor: '#F87171', boxShadow: `0 0 12px ${alpha('#F87171', 0.2)}` },
          }}
        >
          Delete
        </Button>
      )}
    </Box>
  );
};

// â”€â”€â”€ Error / empty guards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ErrorState = ({ message }) => (
  <Container maxWidth="lg" sx={{ mt: 4 }}>
    <FeedbackMessage
      message={message}
      severity="error"
      sx={{
        fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
        background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)',
        boxShadow: '0 4px 20px rgba(239,68,68,0.12)', color: '#fca5a5',
      }}
    />
  </Container>
);

// â”€â”€â”€ BudgetDetails â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BudgetDetails = () => {
  const { id }              = useParams();
  const navigate            = useNavigate();
  const { user: currentUser } = useAuth();

  const [budget,          setBudget]          = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [successMessage,  setSuccessMessage]  = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const canEditBudgetRow = budget ? canEditBudget(currentUser, budget) : false;
  const canDeleteBudget  = canEditBudgetRow;

  // â”€â”€ Effects â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchBudgetDetails();
  }, [id, currentUser, navigate]);

  // â”€â”€ Data fetching â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const fetchBudgetDetails = async () => {
    try {
      const { data } = await axiosInstance.get(`/budgets/${id}`);
      setBudget(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching budget details:', err);
      setError('Failed to fetch budget details. Please try again.');
      if (err.response?.status === 401) return;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBudget = async () => {
    try {
      const itemName = budget?.item ?? 'Budget Item';
      await axiosInstance.delete(`/budgets/${id}`);
      setDeleteDialogOpen(false);
      setSuccessMessage(`Budget item "${itemName}" has been deleted successfully!`);
      setTimeout(() => navigate('/budgets'), 2000);
    } catch (err) {
      console.error('Error deleting budget:', err);
      setError('Failed to delete budget item');
      setDeleteDialogOpen(false);
    }
  };

  // â”€â”€ Guards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (!currentUser)        return <ErrorState message="Please log in to view this page." />;
  if (loading)             return <Loading message="Loading budget details" size={56} variant="primary" />;
  if (error && !budget)    return <ErrorState message={error} />;
  if (!budget)             return <ErrorState message="Budget item not found" />;

  const budgetStatus = Number(budget.percentage) >= 100 ? 'Completed' : Number(budget.percentage) > 0 ? 'In Progress' : 'Planned';
  const statusColor = Number(budget.percentage) >= 100 ? NEON3 : Number(budget.percentage) > 0 ? NEON2 : NEON;
  const relatedClaims = budget.claims ?? [];
  const activeClaimTotal = relatedClaims
    .filter((c) => c.status !== 'rejected')
    .reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title="Budget Details"
        subtitle="View budget information"
        icon={BudgetIcon}
        showBackButton
        onBackClick={() => navigate('/budgets')}
        actionButton={
          <ActionButtons
            canEdit={canEditBudgetRow}
            canDelete={canDeleteBudget}
            onEdit={() => navigate(`/budgets/edit/${id}`)}
            onDelete={() => setDeleteDialogOpen(true)}
          />
        }
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', boxShadow: '0 4px 20px rgba(239,68,68,0.12)', color: '#fca5a5', mb: 3 }}
      />
      <FeedbackMessage
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95), mb: 2 }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BudgetIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {budget.item}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  Requested by {budget.user?.name ?? budget.created_by ?? 'Unknown User'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(statusColor, 0.1),
              border: `1px solid ${alpha(statusColor, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: statusColor }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: statusColor, letterSpacing: '0.05em' }}>
                {budgetStatus}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Estimated Price" value={safePrice(budget.price)} icon={MoneyIcon} color={NEON3} />
            <MetricCard label="Amount" value={budget.amount != null ? Number(budget.amount).toLocaleString() : '-'} icon={NumbersIcon} color={NEON2} />
            <MetricCard label="Percentage" value={budget.percentage != null ? `${Number(budget.percentage).toLocaleString()}%` : '-'} icon={PercentIcon} color={statusColor} />
            <MetricCard label="Unit" value={budget.unit} icon={UnitIcon} color={NEON} />
          </Box>
        </Box>

        {/* â”€â”€ Item Information â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <Section title="Remarks & Notes" icon={ItemIcon} delay={0.1} accent={NEON}>
          <Box sx={{
            px: 2, py: 1.8, borderRadius: '10px',
            borderLeft: `3px solid ${alpha(NEON, 0.4)}`,
            background: alpha(SURFACE_TOP, 0.4),
          }}>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.92rem', color: TEXT_BODY, lineHeight: 1.75 }}>
              {budget.remark || 'No remarks provided.'}
            </Typography>
          </Box>
        </Section>

        <Section title={`Related Claims (${relatedClaims.length})`} icon={ReceiptIcon} delay={0.15} accent={NEON2}>
          {relatedClaims.length === 0 ? (
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.87rem', color: TEXT_MUTED, fontStyle: 'italic' }}>
              No claims are linked to this budget yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: alpha(TEXT_MUTED, 0.95), mb: 0.25 }}>
                Active claims total: <Box component="span" sx={{ color: NEON2, fontWeight: 700 }}>RM {activeClaimTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Box>
                {budget.amount != null ? ` of RM ${Number(budget.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {relatedClaims.map((claim) => (
                  <Box
                    key={claim.id}
                    component="button"
                    type="button"
                    onClick={() => navigate(`/claims/${claim.id}`)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      width: '100%',
                      textAlign: 'left',
                      px: 1.5,
                      py: 1.1,
                      border: 'none',
                      borderRadius: '10px',
                      outline: `1px solid ${alpha(NEON2, 0.12)}`,
                      background: alpha(SURFACE_TOP, 0.35),
                      cursor: 'pointer',
                      transition: 'outline-color 0.18s ease, background 0.18s ease',
                      '&:hover': {
                        outlineColor: alpha(NEON2, 0.32),
                        background: alpha(NEON2, 0.05),
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem', fontWeight: 600, color: TEXT_BODY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {claim.item_bought || claim.description || `Claim #${claim.id}`}
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.73rem', color: TEXT_MUTED, mt: 0.2 }}>
                        {claim.user?.name || claim.full_name || 'Unknown claimant'}
                        {' · '}
                        RM {parseFloat(claim.amount || 0).toFixed(2)}
                        {' · '}
                        {formatClaimDate(claim)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                      <ClaimStatusBadge status={claim.status} />
                      <ChevronRightIcon sx={{ fontSize: 15, color: alpha(NEON2, 0.45) }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Section>

        <SystemInformationSection
          createdBy={budget.user?.name ?? budget.created_by ?? 'Unknown User'}
          createdAt={formatSystemDateTime(budget.created_at)}
          updatedBy={resolveUpdaterName(budget)}
          updatedAt={formatSystemDateTime(budget.updated_at)}
          delay={0.2}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />

      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteBudget}
        itemType="budget item"
        itemName={budget?.item}
        description="This action cannot be undone and will permanently remove the budget item and all associated data."
        confirmLabel="Delete Budget Item"
      />
    </Container>
  );
};

export default BudgetDetails;
